import React from 'react'

import { Badge, Box, Button, DoubleRangeSlider, Text } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { RatingDefaultValues, setLabels } from 'modules/questionnaires/lib/defaultRange'
import { Scale } from 'modules/questionnaires/lib/types'

export interface RatingModalTranslations {
  title: string
  description: string
  save: string
  cancel: string
}

export interface HandleSubmitRating {
  submitting?: boolean
  onSubmit: (scales: Scale[]) => Promise<{ success: boolean }>
}

interface Props {
  translations: RatingModalTranslations
  defaultRatingScale: Scale[]
  handleSubmit: HandleSubmitRating
  children?: React.ReactNode
  onClose: () => void
}

const EditRatingModal: React.FC<Props> = ({
  translations,
  defaultRatingScale,
  handleSubmit,
  children,
  onClose,
}) => {
  const { submitting = false, onSubmit } = handleSubmit
  const rangeDefault = defaultRatingScale.map((r) => r.value)
  const [defaultRange, setDefaultRange] = React.useState<{
    min: number
    max: number
    labels: { value: number; text: string }[]
  }>({
    min: Math.min(...rangeDefault),
    max: Math.max(...rangeDefault),
    labels: setLabels(defaultRatingScale),
  })

  const onSelectRange = ([min, max]: [number, number]) => {
    const minValue = Math.round(min)
    const maxValue = Math.round(max)

    if (minValue === maxValue) return

    setDefaultRange({ ...defaultRange, min: minValue, max: maxValue })
  }

  const updateRatingValueTitle = (
    newTextString: string,
    objectToShow: {
      value: number
      text: string
    }
  ) => {
    const updatedRating = defaultRange.labels.find((item) => item.value === objectToShow.value) || {
      value: objectToShow.value,
      text: '',
    }
    updatedRating.text = newTextString
    setDefaultRange({
      ...defaultRange,
    })
  }

  const objectsToShow = defaultRange.labels.filter((item) => {
    return item?.value >= defaultRange.min && item.value <= defaultRange.max
  })

  const isSubmittingDisabled = Boolean(
    !objectsToShow[0]?.text || !objectsToShow[objectsToShow.length - 1]?.text
  )

  const handleOnClick = async () => {
    const result = await onSubmit(objectsToShow)
    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onClose()
    }
  }

  return (
    <Modal
      illustration="page-edit"
      title={translations.title}
      onClose={onClose}
      footer={
        <Box justifyContent="center" alignItems="center" flexDirection="row" gap="s8">
          <Button type="secondary" onClick={onClose} disabled={submitting}>
            {translations.cancel}
          </Button>
          <Button disabled={isSubmittingDisabled || submitting} onClick={handleOnClick}>
            {translations.save}
          </Button>
        </Box>
      }
    >
      <Box gap="s32">
        <Box gap="s24" paddingBottom="s32">
          <Text.Normal>{translations.description}</Text.Normal>
          <DoubleRangeSlider
            min={RatingDefaultValues.minValue}
            max={RatingDefaultValues.maxValue}
            value={[defaultRange.min, defaultRange.max]}
            onChange={onSelectRange}
          />
        </Box>

        <Box gap="s16">
          {objectsToShow.map((objectToShow) => (
            <Box flexDirection="row" alignItems="center" gap="s16" key={objectToShow.value}>
              <Badge color="viridian100">
                <Text.Normal size="200" weight="semibold" color="viridian1000">
                  {objectToShow.value}
                </Text.Normal>
              </Badge>
              <TextField
                onChange={(value) => updateRatingValueTitle(value, objectToShow)}
                value={objectToShow.text}
                mandatory={
                  objectToShow.value === defaultRange.min || objectToShow.value === defaultRange.max
                }
              />
            </Box>
          ))}
        </Box>

        {children}
      </Box>
    </Modal>
  )
}

export default EditRatingModal
